import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useHistory } from "react-router-dom";
import { LockOutlined, CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import {
  GlobalDetailSectionCard,
  GlobalFieldLabel,
  globalTextFieldSx,
  sharedTableBodyCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  designColors,
  designFontFamily,
  designRadii,
  designShell,
  designType,
  designWeights,
} from "services/constants/designTokens";

export const PAYROLL_HOME_PATH = "/admin/payroll/setup";

// Side panel breadcrumb start, same as the Pay Inputs panels:
// Payroll (opens Payroll home) › Salary › <screen> (closes the panel).
// Clicks are ignored while the panel is saving.
export const useSalaryPanelTrail = (screen, onClose, busy = false) => {
  const history = useHistory();
  return [
    { label: "Payroll", onClick: () => { if (!busy) history.push(PAYROLL_HOME_PATH); } },
    "Salary",
    { label: screen, onClick: () => { if (!busy && onClose) onClose(); } },
  ];
};

// Building blocks shared by the Payroll > Salary screens (Salary Components
// first; Templates, Assign Salary and Revision reuse them as they move to
// the new design).

export const monoSx = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" };

export const SALARY_TONES = {
  green: { bg: "#E7F8EF", fg: "#067647" },
  teal: { bg: designColors.tealTint, fg: designColors.tealDark },
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
  red: { bg: "#FEF2F2", fg: "#B42318" },
  purple: { bg: "#F1EDFF", fg: "#5B4BD5" },
  amber: { bg: "#FFF4E5", fg: "#B54708" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
};

const chipBaseSx = {
  ...designType.captionText,
  fontFamily: designFontFamily,
  fontWeight: designWeights.medium,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: designRadii.compactButton,
  whiteSpace: "nowrap",
  lineHeight: 1.5,
};

export const ToneChip = ({ label, tone = "slate", dot = false, dashed = false, mono = false, sx }) => {
  if (!label) return "–";
  const colors = SALARY_TONES[tone] || SALARY_TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{
        ...chipBaseSx,
        ...(mono ? monoSx : {}),
        backgroundColor: dashed ? designColors.white : colors.bg,
        color: colors.fg,
        border: dashed ? `1px dashed ${designColors.slate300}` : "1px solid transparent",
        ...sx,
      }}
    >
      {dot ? (
        <Mui.Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} />
      ) : null}
      {label}
    </Mui.Box>
  );
};

const STATUS_TONES = { Used: "amber", Unused: "green", System: "slate", Inactive: "slate", Current: "green", Superseded: "slate" };
export const StatusDotChip = ({ status }) => <ToneChip label={status} tone={STATUS_TONES[status] || "slate"} dot />;

export const VersionChip = ({ label, current = false }) => (
  <ToneChip label={label} tone={current ? "teal" : "slate"} sx={{ fontWeight: designWeights.semibold }} />
);

export const SystemTag = ({ label = "System" }) => (
  <Mui.Box
    component="span"
    sx={{ ...chipBaseSx, backgroundColor: designColors.slate100, color: designColors.slate600, py: 0, gap: 0.25 }}
  >
    <LockOutlined sx={{ fontSize: 12 }} />
    {label}
  </Mui.Box>
);

// Underlined tabs with a count badge next to each label.
export const CountTabs = ({ value, onChange, tabs, sx }) => (
  <Mui.Tabs
    value={value}
    onChange={(event, next) => onChange(next)}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    sx={{
      ...designShell.panelTabs,
      mt: 0,
      minHeight: 44,
      borderBottom: `1px solid ${designColors.slate200}`,
      "& .MuiTab-root": { ...designShell.panelTabs["& .MuiTab-root"], minHeight: 44, fontSize: 14, px: 1.5 },
      ...sx,
    }}
  >
    {tabs.map((tab) => (
      <Mui.Tab
        key={tab.value}
        value={tab.value}
        label={
          <Mui.Stack direction="row" spacing={0.75} alignItems="center">
            <span>{tab.label}</span>
            {tab.count == null ? null : (
              <Mui.Box
                component="span"
                sx={{
                  ...designType.captionText,
                  fontWeight: designWeights.semibold,
                  px: 0.75,
                  borderRadius: 10,
                  backgroundColor: value === tab.value ? designColors.tealTint : designColors.slate100,
                  color: value === tab.value ? designColors.tealDark : designColors.slate600,
                }}
              >
                {tab.count}
              </Mui.Box>
            )}
          </Mui.Stack>
        }
      />
    ))}
  </Mui.Tabs>
);

// Grey strip of label/value cells under a panel header (Code, Category,
// Current calculation, Last changed).
export const SummaryStrip = ({ items }) => (
  <Mui.Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: `repeat(${items.length}, minmax(0, 1fr))` },
      border: `1px solid ${designColors.slate200}`,
      borderRadius: designRadii.card,
      backgroundColor: designColors.slateBg,
      overflow: "hidden",
    }}
  >
    {items.map((item, index) => (
      <Mui.Box
        key={item.label}
        sx={{
          px: 2,
          py: 1.5,
          minWidth: 0,
          borderLeft: { sm: index ? `1px solid ${designColors.slate200}` : "none" },
        }}
      >
        <Mui.Typography sx={designType.captionText}>{item.label}</Mui.Typography>
        <Mui.Typography
          noWrap
          title={typeof item.value === "string" ? item.value : undefined}
          sx={{ fontSize: 15, fontWeight: designWeights.semibold, color: designColors.slate900, mt: 0.25, ...(item.mono ? monoSx : {}) }}
        >
          {item.value}
        </Mui.Typography>
      </Mui.Box>
    ))}
  </Mui.Box>
);

// Section card with a grey title bar (shared GlobalDetailSectionCard).
export const SalarySection = ({ title, action, children, muted = false, tone }) => {
  const toneColors =
    tone === "warning"
      ? { border: "#F2B467", head: "#FFF7ED", text: "#B54708" }
      : tone === "danger"
        ? { border: "#FCA5A5", head: "#FEF2F2", text: "#B42318" }
        : null;
  return (
    <Mui.Box
      sx={{
        opacity: muted ? 0.6 : 1,
        "& > div": toneColors ? { borderColor: toneColors.border } : {},
        "& > div > div:first-of-type": { backgroundColor: toneColors?.head || designColors.slateBg },
      }}
    >
      <GlobalDetailSectionCard
        title={title}
        action={action}
        titleSx={{ fontSize: 15, fontWeight: designWeights.semibold, color: toneColors?.text || designColors.slate900 }}
      >
        {children}
      </GlobalDetailSectionCard>
    </Mui.Box>
  );
};

// Read-only label / value pairs, two per row (one on phones).
export const DetailGrid = ({ children }) => (
  <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3, rowGap: 2 }}>
    {children}
  </Mui.Box>
);

export const DetailItem = ({ label, value, helper, mono = false, full = false, labelExtra }) => (
  <Mui.Box sx={{ minWidth: 0, gridColumn: full ? "1 / -1" : undefined }}>
    <Mui.Stack direction="row" spacing={0.5} alignItems="center">
      <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>{label}</Mui.Typography>
      {labelExtra}
    </Mui.Stack>
    <Mui.Box sx={{ fontSize: 15, color: designColors.slate900, mt: 0.25, wordBreak: "break-word", ...(mono ? monoSx : {}) }}>
      {value === "" || value == null ? "–" : value}
    </Mui.Box>
    {helper ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
  </Mui.Box>
);

// Form field: label, input, then helper / error under the input (the
// mockups' layout). Keeping the helper below means paired fields in a row
// always start their inputs at the same height.
export const FormField = ({ label, helper, error, children }) => (
  <Mui.Box sx={{ minWidth: 0 }}>
    <GlobalFieldLabel>{label}</GlobalFieldLabel>
    {children}
    {error ? (
      <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.5 }}>{error}</Mui.Typography>
    ) : null}
    {helper ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>{helper}</Mui.Typography> : null}
  </Mui.Box>
);

// Switch with its Yes / No label.
export const YesNoSwitch = ({ checked, onChange, disabled }) => (
  <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 40 }}>
    <Mui.Switch
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      sx={{
        "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: designColors.teal },
      }}
    />
    <Mui.Typography sx={designType.tableBodyCell}>{checked ? "Yes" : "No"}</Mui.Typography>
  </Mui.Stack>
);

// Fixed Amount / Percentage / Formula choice as radio cards. Read-only when
// onChange is not passed.
export const MethodCards = ({ value, options, onChange, disabled }) => (
  <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${options.length}, minmax(0, 1fr))` }, gap: 1.5 }}>
    {options.map((option) => {
      const selected = option.value === value;
      const interactive = Boolean(onChange) && !disabled;
      return (
        <Mui.Box
          key={option.value}
          component={interactive ? "button" : "div"}
          type={interactive ? "button" : undefined}
          onClick={interactive ? () => onChange(option.value) : undefined}
          aria-pressed={interactive ? selected : undefined}
          sx={{
            all: "unset",
            boxSizing: "border-box",
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
            p: 1.5,
            borderRadius: designRadii.card,
            border: `${selected ? 2 : 1}px solid ${selected ? designColors.teal : designColors.slate200}`,
            backgroundColor: selected ? designColors.tealTint : designColors.white,
            cursor: interactive ? "pointer" : "default",
            "&:focus-visible": { outline: `2px solid ${designColors.teal}`, outlineOffset: 2 },
          }}
        >
          <Mui.Radio checked={selected} size="small" tabIndex={-1} disableRipple sx={{ p: 0, mt: 0.25, color: designColors.slate400, "&.Mui-checked": { color: designColors.tealDark } }} />
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{option.label}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{option.helper}</Mui.Typography>
          </Mui.Box>
        </Mui.Box>
      );
    })}
  </Mui.Box>
);

const editorChipSx = (tone) => ({
  ...monoSx,
  minWidth: 0,
  px: 1,
  py: 0.25,
  fontSize: 12.5,
  lineHeight: 1.6,
  textTransform: "none",
  borderRadius: designRadii.compactButton,
  border: `1px solid ${tone === "code" ? "#A7E3CB" : designColors.slate300}`,
  backgroundColor: tone === "code" ? "#E7F8EF" : designColors.white,
  color: tone === "code" ? "#067647" : designColors.slate700,
});

const OPERATORS = [
  { label: "+", insert: " + " },
  { label: "−", insert: " - " },
  { label: "×", insert: " * " },
  { label: "÷", insert: " / " },
  { label: "%", insert: "%" },
  { label: "( )", insert: "()", caret: 1 },
];

// Formula text box with clickable codes / functions / operators that insert
// at the cursor, and the validation line under it.
export const FormulaEditor = ({ value, onChange, codes, functions, check, error, disabled }) => {
  const inputRef = useRef(null);
  const insert = (text, caretOffset) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    const caret = start + (caretOffset ?? text.length);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(caret, caret);
    });
  };
  const row = (label, items) => (
    <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
      <Mui.Typography sx={{ ...designType.captionText, width: 84, letterSpacing: "0.04em", fontWeight: designWeights.semibold }}>{label}</Mui.Typography>
      {items}
    </Mui.Stack>
  );
  return (
    <Mui.Box>
      <Mui.Box sx={{ border: `1px solid ${error ? "#DC2626" : designColors.slate300}`, borderRadius: designRadii.card, overflow: "hidden" }}>
        <Mui.InputBase
          inputRef={inputRef}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          multiline
          minRows={2}
          placeholder="e.g. MIN(BASIC * 10%, 2000)"
          inputProps={{ "aria-label": "Formula", spellCheck: false }}
          sx={{ width: "100%", px: 1.5, py: 1, fontSize: 15, ...monoSx }}
        />
        <Mui.Stack spacing={1} sx={{ px: 1.5, py: 1.25, backgroundColor: designColors.slateBg, borderTop: `1px solid ${designColors.slate200}` }}>
          {row("CODES", codes.map((code) => (
            <Mui.Button key={code} size="small" disabled={disabled} onClick={() => insert(code)} sx={editorChipSx("code")}>{code}</Mui.Button>
          )))}
          {row("FUNCTIONS", functions.map((fn) => (
            <Mui.Button key={fn} size="small" disabled={disabled} onClick={() => insert(fn === "IF" ? "IF(, , )" : `${fn}()`, fn.length + 1)} sx={editorChipSx()}>{fn}</Mui.Button>
          )))}
          {row("OPERATORS", OPERATORS.map((op) => (
            <Mui.Button key={op.label} size="small" disabled={disabled} onClick={() => insert(op.insert, op.caret)} sx={editorChipSx()}>{op.label}</Mui.Button>
          )))}
        </Mui.Stack>
      </Mui.Box>
      {value.trim() && check ? (
        <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, color: check.valid ? "#067647" : "#DC2626" }}>
          {check.valid ? <CheckCircleOutline sx={{ fontSize: 16 }} /> : <ErrorOutline sx={{ fontSize: 16 }} />}
          <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.semibold }}>{check.message}</Mui.Typography>
        </Mui.Stack>
      ) : null}
    </Mui.Box>
  );
};

// Light grey box for "Preview at GROSS ₹30,000 … BASIC = ₹15,000" and the
// formula test breakdown.
export const PreviewBox = ({ children, sx }) => (
  <Mui.Box sx={{ backgroundColor: designColors.slateBg, border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, px: 2, py: 1.5, ...sx }}>
    {children}
  </Mui.Box>
);

export const PreviewLine = ({ label, value, strong = false, mono = false, divider = false }) => (
  <Mui.Stack
    direction="row"
    justifyContent="space-between"
    spacing={2}
    sx={{ py: 0.5, ...(divider ? { borderTop: `1px dashed ${designColors.slate300}`, mt: 0.75, pt: 1 } : {}) }}
  >
    <Mui.Typography sx={{ fontSize: strong ? 16 : 14, fontWeight: strong ? designWeights.semibold : 400, color: designColors.slate700, ...(mono ? monoSx : {}) }}>{label}</Mui.Typography>
    <Mui.Typography sx={{ fontSize: strong ? 16 : 14, fontWeight: strong ? designWeights.semibold : 400, color: designColors.slate900, whiteSpace: "nowrap" }}>{value}</Mui.Typography>
  </Mui.Stack>
);

// Field | before | after table; changed rows are highlighted.
export const DiffTable = ({ beforeLabel, afterLabel, rows }) => (
  <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflow: "hidden" }}>
    <Mui.Table size="small" sx={{ tableLayout: "fixed" }}>
      <Mui.TableHead>
        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
          {["Field", beforeLabel, afterLabel].map((head) => (
            <Mui.TableCell key={head} sx={{ ...designShell.tableHeadCellBase, py: 1 }}>{head}</Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody>
        {rows.map((row) => (
          <Mui.TableRow key={row.label} sx={{ backgroundColor: row.changed ? "#FFF7E6" : undefined, "&:last-child td": { borderBottom: "none" } }}>
            <Mui.TableCell sx={{ fontSize: 14, fontWeight: row.changed ? designWeights.semibold : 400, color: designColors.slate900 }}>{row.label}</Mui.TableCell>
            <Mui.TableCell sx={{ fontSize: 14, color: designColors.slate600, wordBreak: "break-word", ...(row.mono ? monoSx : {}) }}>{row.before}</Mui.TableCell>
            <Mui.TableCell sx={{ fontSize: 14, color: designColors.slate900, fontWeight: row.changed ? designWeights.semibold : 400, wordBreak: "break-word", ...(row.mono ? monoSx : {}) }}>{row.after}</Mui.TableCell>
          </Mui.TableRow>
        ))}
      </Mui.TableBody>
    </Mui.Table>
  </Mui.Box>
);

// Small pill used for counts like "4 on V3" / "2 can upgrade to V3".
export const CountPill = ({ label, tone = "slate" }) => (
  <ToneChip label={label} tone={tone} sx={{ fontSize: 13, px: 1.25, py: 0.5, borderRadius: 10 }} />
);

// Panel header: icon tile, title with chips beside it, one subtitle line.
export const PanelTitleBlock = ({ icon, title, chips, subtitle, muted = false }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
    <Mui.Box
      sx={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: designRadii.card,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: muted ? designColors.slate100 : designColors.tealTint,
        color: muted ? designColors.slate600 : designColors.tealDark,
      }}
    >
      {icon}
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Mui.Typography component="h2" sx={{ ...designType.panelTitle, fontSize: 20 }}>{title}</Mui.Typography>
        {chips}
      </Mui.Stack>
      {subtitle ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{subtitle}</Mui.Typography> : null}
    </Mui.Box>
  </Mui.Stack>
);

// ---- Panel helpers shared by the Salary side panels ----

export const Required = () => <Mui.Box component="span" sx={{ color: "#DC2626" }}> *</Mui.Box>;

// Footer: muted note on the left, buttons on the right.
export const PanelFooter = ({ note, children }) => (
  <Mui.Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={1.5}
    alignItems={{ xs: "stretch", sm: "center" }}
    justifyContent="space-between"
    sx={{ width: "100%" }}
  >
    <Mui.Box sx={{ ...designType.helperText, minWidth: 0 }}>{note}</Mui.Box>
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
      {children}
    </Mui.Stack>
  </Mui.Stack>
);

export const dangerButtonSx = { backgroundColor: "#DC2626", "&:hover": { backgroundColor: "#B91C1C" } };

export const multilineFieldSx = { ...globalTextFieldSx, width: "100%" };

export const searchFieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root": { ...globalTextFieldSx["& .MuiOutlinedInput-root"], height: 40, fontSize: 14 },
};

export const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "normal", lineHeight: 1.3 };
export const tableCellSx = { ...sharedTableBodyCellSx, whiteSpace: "normal", verticalAlign: "middle", py: 1.25 };

// fit (side panels): fills the container, and every column is as wide as
// its content (nothing cut off or wrapped). When that is wider than the
// panel, the table scrolls sideways. head.width is then a minimum.
export const SimpleTable = ({ heads, children, minWidth = 640, fit = false }) => (
  <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: "8px", overflowX: "auto", backgroundColor: designColors.white }}>
    <Mui.Table
      size="small"
      sx={
        fit
          ? {
              width: "100%",
              "& th, & td": { whiteSpace: "nowrap" },
            }
          : { minWidth }
      }
    >
      <Mui.TableHead>
        <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
          {heads.map((head) => (
            <Mui.TableCell
              key={head.label}
              align={head.align}
              sx={{
                ...tableHeadSx,
                ...(fit ? { minWidth: head.width, whiteSpace: "nowrap" } : { width: head.width }),
              }}
            >
              {head.label}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
      </Mui.TableHead>
      <Mui.TableBody sx={{ "& tr:last-child td": { borderBottom: "none" } }}>{children}</Mui.TableBody>
    </Mui.Table>
  </Mui.Box>
);

export const EmptyRow = ({ colSpan, text }) => (
  <Mui.TableRow>
    <Mui.TableCell colSpan={colSpan} sx={{ ...tableCellSx, textAlign: "center", color: designColors.slate600, py: 3 }}>
      {text}
    </Mui.TableCell>
  </Mui.TableRow>
);

export const downloadCsv = (fileName, header, rows) => {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map((row) => row.map(escape).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

// ---- List hooks shared by the Salary list screens ----

// Measured height of a sticky element (the CustomFilter bar), so the table
// header can sit right under it.
export const useMeasuredHeight = () => {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);
  const ref = useCallback((element) => setNode(element), []);
  useEffect(() => {
    if (!node) return undefined;
    const update = () => setHeight(node.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [ref, height];
};

// CustomFilter reports its result one render after `rows` changes; only
// trust it when it was computed from the current rows (Users pattern).
export const useFilteredRows = (rows) => {
  const [filtered, setFiltered] = useState(null);
  const sourceRef = useRef(null);
  const visible = filtered !== null && sourceRef.current === rows ? filtered : rows;
  const onChange = useCallback(
    (data) => {
      sourceRef.current = rows;
      setFiltered(data);
    },
    [rows],
  );
  return [visible, onChange];
};

